import React, { useRef, useEffect } from 'react';
import { Modal, Box, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { styled, keyframes } from '@mui/material/styles';
import { TourImageDetail } from '@/types/tourBooking';

interface FullScreenImagesProps {
  open: boolean;
  images: TourImageDetail[];
  selectedIdx: number;
  onClose: () => void;
  onSelect: (idx: number) => void;
}

const ModalContent = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100vw',
  height: '100vh',
  backgroundColor: 'rgba(0,0,0,0.85)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  animation: 'animate-fade-in 300ms cubic-bezier(0.4,0,0.2,1)',
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: 24,
  right: 24,
  color: '#fff',
  zIndex: 10,
}));

const MainImageBox = styled(Box)(({ theme }) => ({
  width: '65vw',
  height: '75vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    width: '90vw',
    height: '40vh',
  },
  [theme.breakpoints.between('sm', 'md')]: {
    width: '80vw',
    height: '60vh',
  },
}));

const MainImage = styled('img')(({ theme }) => ({
  maxWidth: '100%',
  maxHeight: '100%',
  borderRadius: 16,
  objectFit: 'contain',
  boxShadow: '0 4px 32px 0 rgba(0,0,0,0.25)',
}));

const ThumbsBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(4),
  overflowX: 'auto',
  paddingBottom: theme.spacing(0),
  maxWidth: '60vw',
  margin: '0',
  [theme.breakpoints.down('sm')]: {
    maxWidth: '90vw',
  },
  [theme.breakpoints.between('sm', 'md')]: {
    maxWidth: '80vw',
  },
}));

const ThumbImage = styled('img')<{ selected: boolean }>(({ theme, selected }) => ({
  width: 88,
  height: 56,
  objectFit: 'cover',
  borderRadius: 8,
  border: selected ? '2px solid #fff' : '2px solid transparent',
  cursor: 'pointer',
  opacity: selected ? 1 : 0.6,
  boxShadow: selected ? '0 2px 8px 0 rgba(0,0,0,0.15)' : 'none',
  transition: 'border 0.2s',
  '&:hover': {
    opacity: 0.8,
  },
}));

const FullScreenImages: React.FC<FullScreenImagesProps> = ({ open, images, selectedIdx, onClose, onSelect }) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const mainImageBoxRef = useRef<HTMLDivElement>(null);
  const thumbsBoxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Tab') {
        // Get all focusable elements inside the modal
        const focusable = modalRef.current?.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (!focusable || focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;
        if (!e.shiftKey && active === last) {
          e.preventDefault();
          onClose();
        } else if (e.shiftKey && active === first) {
          e.preventDefault();
          onClose();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  // Dismiss modal if click is outside MainImageBox and ThumbsBox
  const handleModalContentClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!mainImageBoxRef.current || !thumbsBoxRef.current) return;
    if (
      !mainImageBoxRef.current.contains(e.target as Node) &&
      !thumbsBoxRef.current.contains(e.target as Node)
    ) {
      onClose();
    }
  };

  return (
    <Modal open={open} onClose={onClose} sx={{ zIndex: 2000 }}>
      <ModalContent ref={modalRef} onClick={handleModalContentClick}>
        <CloseButton onClick={onClose}>
          <CloseIcon fontSize="large" />
        </CloseButton>
        <MainImageBox ref={mainImageBoxRef}>
          <MainImage
            src={images[selectedIdx].url}
            alt={images[selectedIdx].alt ?? `full-${selectedIdx}`}
          />
        </MainImageBox>
        <ThumbsBox ref={thumbsBoxRef}>
          {images.map((img, idx) => (
            <ThumbImage
              key={`img-${idx}`}
              src={img.url}
              alt={img.alt ?? `thumb-${idx}`}
              onClick={() => onSelect(idx)}
              selected={idx === selectedIdx}
            />
          ))}
        </ThumbsBox>
      </ModalContent>
    </Modal>
  );
};

export default FullScreenImages;
